/**
 * useResponsive Hook
 * 
 * Manages responsive state for the application.
 * Provides isCompact state and COMPACT_BP constant.
 * 
 * @module hooks/useResponsive
 * 
 * Breakpoint Strategy:
 * - COMPACT_BP = 900px: Desktop panel (master UX) shows on tablets; BottomSheet only on phones
 * - Below 900px: Compact mode (mobile/phone UI with BottomSheet)
 * - 900px and above: Desktop mode (DesktopCartPanel)
 * 
 * Usage:
 * ```ts
 * const { isCompact, COMPACT_BP } = useResponsive();
 * 
 * // Conditional rendering based on viewport
 * return isCompact ? <BottomSheet /> : <DesktopCartPanel />;
 * ```
 */
import { useState, useEffect } from 'react';

/** Breakpoint for compact vs desktop mode (900px) */
export const COMPACT_BP: number = 900;

/**
 * Return type for useResponsive hook
 */
export interface UseResponsiveReturn {
  /** true if viewport width < COMPACT_BP */
  isCompact: boolean;
  /** The breakpoint value (900) */
  COMPACT_BP: number;
}

/**
 * Hook for managing responsive state
 * 
 * @returns Responsive state with isCompact boolean and COMPACT_BP constant
 */
export function useResponsive(): UseResponsiveReturn {
  // Initialize with current window width
  const [isCompact, setIsCompact] = useState<boolean>(
    typeof window !== 'undefined' ? window.innerWidth < COMPACT_BP : false
  );

  // Resize listener for responsive mode switching
  useEffect(() => {
    let rafId: number | null = null;

    const apply = () => {
      const el = document.documentElement;
      const isNowCompact = window.innerWidth < COMPACT_BP;
      
      // Apply CSS class for compact mode styling
      if (isNowCompact) {
        el.classList.add("compact");
      } else {
        el.classList.remove("compact");
      }
      
      // Update state for conditional rendering
      setIsCompact(isNowCompact);
    };

    // Apply on mount
    apply();

    // Throttle resize events to one per animation frame
    const onResize = () => {
      if (rafId != null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        apply();
      });
    };

    // Listen for resize events
    window.addEventListener("resize", onResize);
    
    // Cleanup
    return () => {
      window.removeEventListener("resize", onResize);
      if (rafId != null) cancelAnimationFrame(rafId);
    };
  }, []);

  return {
    isCompact,
    COMPACT_BP
  };
}

export default useResponsive;
